import React, { useState, useEffect, useRef } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { messagingApi, uploadApi } from '../lib/api';
import analytics from '../lib/analytics';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { ChevronLeft, Send, MessageCircle, ImageIcon, Info } from 'lucide-react';

const Messages = () => {
  const { t } = useLanguage();
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [conversations, setConversations] = useState([]);
  const [selectedConv, setSelectedConv] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const fileInputRef = useRef(null);
  const isPaymentProof = searchParams.get('payment_proof') === '1';
  const messagesEndRef = useRef(null);
  const initialLoadDone = useRef(false);

  useEffect(() => {
    fetchConversations();
  }, []);

  // Handle deep link: /messages?to=userId&activity=activityId
  useEffect(() => {
    if (!initialLoadDone.current || loading) return;
    const toUser = searchParams.get('to');
    const activityId = searchParams.get('activity');
    if (toUser) {
      // Find existing conversation or create placeholder
      const existing = conversations.find(c => 
        c.participants?.includes(toUser) && 
        (!activityId || c.activity_id === activityId)
      );
      if (existing) {
        openConversation(existing);
      } else {
        // Create a temporary conversation placeholder
        setSelectedConv({
          conversation_id: null,
          participants: [user?.user_id, toUser],
          activity_id: activityId,
          other_user: { user_id: toUser, name: 'Creador' }
        });
        setMessages([]);
      }
    }
  }, [loading, searchParams]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const fetchConversations = async () => {
    try {
      const res = await messagingApi.getConversations();
      setConversations(res.data);
    } catch (error) {
      console.warn("Messages error:", error.message);
    } finally {
      setLoading(false);
      initialLoadDone.current = true;
    }
  };

  const openConversation = async (conv) => {
    setSelectedConv(conv);
    try {
      const res = await messagingApi.getMessages(conv.conversation_id);
      setMessages(res.data);
      // Update unread count locally
      setConversations(prev => prev.map(c => 
        c.conversation_id === conv.conversation_id ? { ...c, unread_count: 0 } : c
      ));
    } catch (error) {
      toast.error('Error al cargar mensajes');
    }
  };

  const handleSend = async (imageUrl = null) => {
    if (!imageUrl && !newMessage.trim()) return;
    if (!selectedConv) return;
    setSending(true);
    try {
      const otherUserId = selectedConv.other_user?.user_id;
      const res = await messagingApi.sendMessage({
        recipient_id: otherUserId,
        activity_id: selectedConv.activity_id,
        content: imageUrl ? '' : newMessage.trim(),
        image_url: imageUrl || ''
      });
      setMessages(prev => [...prev, res.data]);
      setNewMessage('');
      analytics.sendMessage(otherUserId);
      
      // If this was a new conversation, refresh the list
      if (!selectedConv.conversation_id) {
        const convRes = await messagingApi.getConversations();
        setConversations(convRes.data);
        const newConv = convRes.data.find(c => c.participants?.includes(otherUserId));
        if (newConv) setSelectedConv(newConv);
      } else {
        setConversations(prev => prev.map(c => 
          c.conversation_id === selectedConv.conversation_id 
            ? { ...c, last_message: newMessage.trim(), last_message_at: new Date().toISOString() }
            : c
        ));
      }
    } catch (error) {
      toast.error('Error al enviar mensaje');
    } finally {
      setSending(false);
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Solo se permiten imágenes');
      return;
    }
    setUploadingImage(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await uploadApi.upload(formData);
      const url = res.data?.url || res.data?.file_url;
      if (url) {
        await handleSend(url);
      }
    } catch (err) {
      toast.error('Error al subir imagen');
    } finally {
      setUploadingImage(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-20 flex items-center justify-center">
        <div className="w-10 h-10 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className={`bg-white ${selectedConv ? '' : 'min-h-screen pb-32 md:pb-8'}`}>
      <div className={`max-w-3xl mx-auto ${selectedConv ? 'px-4 md:px-6' : ''}`}>
        {!selectedConv ? (
          <>
            <div className="sticky top-14 md:top-11 z-30 bg-white pt-4 px-4 md:px-6 pb-4">
              <h1 className="font-rubik text-2xl font-bold text-gray-500" data-testid="messages-title">
                {t('messages.title')}
              </h1>
            </div>
            
            <div className="px-4 md:px-6">
            {conversations.length === 0 ? (
              <div className="text-center py-16">
                <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-4">
                  <MessageCircle className="w-8 h-8 text-gray-300" />
                </div>
                <p className="text-gray-500 font-medium mb-2">{t('messages.empty')}</p>
                <p className="text-gray-400 text-sm">{t('messages.emptyDesc')}</p>
              </div>
            ) : (
              <div className="space-y-2">
                {conversations.map((conv) => (
                  <button
                    key={conv.conversation_id}
                    onClick={() => openConversation(conv)}
                    className="w-full flex items-center gap-3 p-4 rounded-xl hover:bg-gray-50 transition-colors text-left border border-gray-100"
                    data-testid={`conversation-${conv.conversation_id}`}
                  >
                    <Avatar className="h-12 w-12 flex-shrink-0">
                      <AvatarImage src={conv.other_user?.profile_image} />
                      <AvatarFallback className="bg-[#7f9df1] text-white">
                        {conv.other_user?.name?.charAt(0)?.toUpperCase()}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between">
                        <p className="font-medium text-gray-500 truncate">{conv.other_user?.name}</p>
                        {conv.unread_count > 0 && (
                          <span className="bg-[#22BCB9] text-white text-xs rounded-full w-5 h-5 flex items-center justify-center flex-shrink-0">
                            {conv.unread_count}
                          </span>
                        )}
                      </div>
                      {conv.activity_title && (
                        <p className="text-xs text-[#7f9df1] truncate">{conv.activity_title}</p>
                      )}
                      <p className="text-sm text-gray-400 truncate">{conv.last_message}</p>
                    </div>
                  </button>
                ))}
              </div>
            )}
            </div>
          </>
        ) : (
          <div className="fixed inset-x-0 top-14 md:top-11 bottom-20 md:bottom-0 bg-white z-10">
            <div className="max-w-3xl mx-auto px-4 md:px-6 h-full flex flex-col">
            {/* Chat header */}
            <div className="bg-white flex items-center gap-3 py-3 border-b border-gray-100 flex-shrink-0">
              <button
                onClick={() => {
                  const fromActivity = searchParams.get('activity');
                  if (fromActivity) {
                    navigate(`/activity/${fromActivity}`, { replace: true });
                  } else {
                    setSelectedConv(null);
                  }
                }}
                className="p-2 rounded-full hover:bg-gray-100 transition-colors"
                data-testid="back-to-conversations"
              >
                <ChevronLeft className="w-5 h-5 text-gray-500" />
              </button>
              <Avatar className="h-10 w-10">
                <AvatarImage src={selectedConv.other_user?.profile_image} />
                <AvatarFallback className="bg-[#7f9df1] text-white">
                  {selectedConv.other_user?.name?.charAt(0)?.toUpperCase()}
                </AvatarFallback>
              </Avatar>
              <div>
                <p className="font-medium text-gray-500">{selectedConv.other_user?.name}</p>
                {selectedConv.activity_title && (
                  <p className="text-xs text-[#7f9df1]">{selectedConv.activity_title}</p>
                )}
              </div>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto space-y-3 py-4" data-testid="messages-list">
              {/* System message for payment proof */}
              {isPaymentProof && messages.length === 0 && (
                <div className="flex justify-center mb-4" data-testid="payment-proof-hint">
                  <div className="bg-blue-50 border border-blue-100 rounded-xl px-4 py-3 max-w-[85%] flex items-start gap-2.5">
                    <Info className="w-4 h-4 text-blue-500 mt-0.5 flex-shrink-0" />
                    <p className="text-xs text-blue-600 leading-relaxed">
                      Para confirmar tu pago de <strong>{selectedConv.activity_title || 'la actividad'}</strong>, envía una imagen de tu comprobante de pago en este chat. El creador revisará y confirmará tu reserva.
                    </p>
                  </div>
                </div>
              )}
              {messages.length === 0 && !isPaymentProof && (
                <p className="text-center text-gray-400 py-8 text-sm">Inicia la conversación</p>
              )}
              {messages.map((msg) => {
                const isMine = msg.sender_id === user?.user_id;
                return (
                  <div
                    key={msg.message_id}
                    className={`flex ${isMine ? 'justify-end' : 'justify-start'}`}
                  >
                    <div
                      className={`max-w-[75%] px-4 py-2.5 rounded-2xl text-sm ${
                        isMine
                          ? 'bg-[#7f9df1] text-white rounded-br-md'
                          : 'bg-gray-100 text-gray-600 rounded-bl-md'
                      }`}
                      data-testid={`message-${msg.message_id}`}
                    >
                      {msg.image_url && (
                        <img 
                          src={msg.image_url} 
                          alt="Imagen" 
                          className="rounded-lg mb-2 max-w-[200px] max-h-48 object-cover cursor-pointer hover:opacity-90 transition-opacity"
                          onClick={() => window.open(msg.image_url, '_blank')}
                          data-testid={`message-image-${msg.message_id}`}
                        />
                      )}
                      {msg.content && <p>{msg.content}</p>}
                      <p className={`text-[10px] mt-1 ${isMine ? 'text-white/60' : 'text-gray-400'}`}>
                        {new Date(msg.created_at).toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' })}
                      </p>
                    </div>
                  </div>
                );
              })}
              <div ref={messagesEndRef} />
            </div>

            {/* Input */}
            <div className="flex-shrink-0 pt-2 pb-3">
              <input
                type="file"
                accept="image/*"
                ref={fileInputRef}
                onChange={handleImageUpload}
                className="hidden"
                data-testid="image-upload-input"
              />
              <div className="flex items-center gap-2 bg-white border border-gray-200 rounded-full px-4 py-2 shadow-sm">
                {user?.email_verified === false ? (
                  <p className="text-xs text-amber-600 flex-1 text-center py-1">Verifica tu correo para enviar mensajes</p>
                ) : (
                  <>
                <button
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploadingImage}
                  className="p-1 rounded-full hover:bg-gray-100 text-gray-400 hover:text-gray-500 transition-colors flex-shrink-0"
                  data-testid="attach-image-btn"
                >
                  <ImageIcon className="w-5 h-5" />
                </button>
                <Input
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleSend()}
                  placeholder={uploadingImage ? "Subiendo imagen..." : "Escribe un mensaje..."}
                  className="border-none shadow-none focus-visible:ring-0 text-sm"
                  disabled={uploadingImage}
                  data-testid="message-input"
                />
                <Button
                  onClick={() => handleSend()}
                  disabled={!newMessage.trim() || sending || uploadingImage}
                  size="sm"
                  className="rounded-full bg-[#7f9df1] hover:bg-[#6b8be0] h-8 w-8 p-0 flex-shrink-0"
                  data-testid="send-message-btn"
                >
                  <Send className="w-4 h-4" />
                </Button>
                  </>
                )}
              </div>
            </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Messages;
